/* ═══════════════════════════════════════════════════════════════
   The Designer — the Navigation palette.

   Word's navigation pane, and the same thing the master editors show:
   the heading map of the section that is open. It reads the LIVE
   ProseMirror document and the ONE numbering brain, so a heading that
   appears here appears on the paper with exactly that number. Clicking
   a row jumps the canvas to it and puts the caret there — navigating is
   never a separate mode from writing.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import type { Editor } from '@tiptap/react';
import type { ContentBlock, StandardConfig } from '../../shared/types';
import { computeNumbering } from '../../shared/body-layout';
import EmptyState from '../components/EmptyState';

export interface NavItem {
  id: string;
  pos: number;
  level: number;
  prefix: string;
  text: string;
}

/** Levels 1–3 are the section's skeleton — the default map. */
const SHALLOW_DEPTH = 3;

const NavIcon = (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4 6h16M4 12h11M4 18h7" />
  </svg>
);

/** Read the heading map out of the editor's current document. */
export function readNavItems(editor: Editor | null, forma: StandardConfig | null): NavItem[] {
  if (!editor) return [];
  const blocks: ContentBlock[] = [];
  const positions: number[] = [];
  const texts: string[] = [];
  editor.state.doc.forEach((node, offset) => {
    blocks.push({
      id: (node.attrs.blockId as string) || String(offset),
      level: (node.attrs.level as number) || 1,
      text: '',
      continuation: !!node.attrs.continuation,
      excluded: !!node.attrs.excluded,
    });
    positions.push(offset);
    texts.push(node.textContent || (node.attrs.text as string) || '');
  });
  const refs = computeNumbering(blocks, forma);
  return blocks.map((b, i) => ({
    id: b.id,
    pos: positions[i],
    level: b.level,
    prefix: refs[i]?.prefix || '',
    text: texts[i],
  }));
}

/** The heading map of a block list that is NOT a live editor (Spec Studio's
 *  Track changes view). `pos` is the block's index in the list. */
export function navItemsFromBlocks(
  blocks: ContentBlock[],
  forma: StandardConfig | null
): NavItem[] {
  const refs = computeNumbering(
    blocks.map((b) => ({ ...b, text: '' })),
    forma
  );
  return blocks.map((b, i) => ({
    id: b.id,
    pos: i,
    level: b.level || 1,
    prefix: refs[i]?.prefix || '',
    text: b.text || '',
  }));
}

export function NavigationPalette({
  editor,
  forma,
  onJump,
  blocks,
}: {
  editor: Editor | null;
  forma: StandardConfig | null;
  /** Scroll the canvas to a block and place the caret in it. */
  onJump: (item: NavItem) => void;
  /** When set, the map reads THESE blocks instead of the editor (a view
   *  that renders without a live editor, e.g. Track changes). */
  blocks?: ContentBlock[] | null;
}) {
  const [query, setQuery] = React.useState('');
  const [allLevels, setAllLevels] = React.useState(false);
  const listRef = React.useRef<HTMLDivElement>(null);

  // The map follows the document: typing a new heading makes it appear.
  const [version, bump] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!editor) return;
    const onUpdate = () => bump();
    editor.on('update', onUpdate);
    return () => {
      editor.off('update', onUpdate);
    };
  }, [editor]);

  const items = React.useMemo(
    () => (blocks ? navItemsFromBlocks(blocks, forma) : readNavItems(editor, forma)),
    // `version` is the document's revision — it is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, forma, version, blocks]
  );

  // Per-node collapse, alongside the depth toggle (not instead of it). Ids,
  // not positions — a collapsed heading stays collapsed while you type
  // elsewhere in the document. Session-local: nothing here persists.
  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(() => new Set());
  const toggleCollapsed = (id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const depthFiltered = React.useMemo(
    () => items.filter((it) => allLevels || it.level <= SHALLOW_DEPTH),
    [items, allLevels]
  );

  /** A row can collapse when the very next row (in the depth-filtered list —
   *  what the eye actually sees) is deeper than it. */
  const hasChildren = React.useMemo(() => {
    const out = new Set<string>();
    for (let i = 0; i < depthFiltered.length - 1; i++) {
      if (depthFiltered[i + 1].level > depthFiltered[i].level) out.add(depthFiltered[i].id);
    }
    return out;
  }, [depthFiltered]);

  const q = query.trim().toLowerCase();
  const filtered = React.useMemo(() => {
    // A search answers "where is it", so it ignores collapse — hiding a match
    // inside a folded subtree would make the filter box lie.
    if (q) {
      return depthFiltered.filter(
        (it) => it.text.toLowerCase().includes(q) || it.prefix.toLowerCase().includes(q)
      );
    }
    const out: NavItem[] = [];
    let closedAtLevel: number | null = null;
    for (const it of depthFiltered) {
      if (closedAtLevel !== null) {
        if (it.level > closedAtLevel) continue; // inside the collapsed subtree
        closedAtLevel = null;
      }
      out.push(it);
      if (hasChildren.has(it.id) && collapsed.has(it.id)) closedAtLevel = it.level;
    }
    return out;
  }, [depthFiltered, q, collapsed, hasChildren]);

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const rows = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>('[data-nav-item]') || []
    );
    const idx = rows.findIndex((el) => el === document.activeElement);
    const next = e.key === 'ArrowDown' ? idx + 1 : idx - 1;
    rows[Math.max(0, Math.min(rows.length - 1, next))]?.focus();
  };

  return (
    <div className="dsg-pal dsg-nav">
      <div className="dsg-pal-controls">
        <label className="sr-only" htmlFor="dsg-nav-filter">
          Filter headings
        </label>
        <input
          id="dsg-nav-filter"
          type="search"
          className="dsg-input"
          placeholder="Find a heading"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button
          type="button"
          className={`dsg-btn dsg-btn-sm${allLevels ? ' is-on' : ''}`}
          aria-pressed={allLevels}
          onClick={() => setAllLevels((v) => !v)}
          title={
            allLevels
              ? 'Show only the section skeleton (levels 1–3)'
              : 'Show every paragraph, all nine levels'
          }
        >
          {allLevels ? 'Levels 1–3' : 'All levels'}
        </button>
      </div>

      <div
        ref={listRef}
        className="dsg-nav-list"
        role="listbox"
        aria-label="Headings in this section"
        onKeyDown={onListKeyDown}
      >
        {filtered.length === 0 ? (
          <div className="dsg-nav-empty">
            <EmptyState
              icon={NavIcon}
              title={items.length ? 'Nothing matches that' : 'Nothing to navigate yet'}
              subtitle={
                items.length
                  ? 'Clear the filter, or switch to all levels.'
                  : 'Headings appear here as you write them.'
              }
            />
          </div>
        ) : (
          filtered.map((it) => {
            const expandable = hasChildren.has(it.id);
            const expanded = !collapsed.has(it.id);
            return (
              <div key={it.id} className="dsg-nav-row">
                {expandable ? (
                  <button
                    type="button"
                    className="dsg-nav-chevron"
                    aria-expanded={expanded}
                    aria-label={
                      expanded
                        ? `Collapse ${it.text || 'this heading'}`
                        : `Expand ${it.text || 'this heading'}`
                    }
                    onClick={() => toggleCollapsed(it.id)}
                  >
                    {expanded ? (
                      <Icon icon={ChevronDown} size="md" aria-hidden="true" />
                    ) : (
                      <Icon icon={ChevronRight} size="md" aria-hidden="true" />
                    )}
                  </button>
                ) : (
                  <span className="dsg-nav-chevron-spacer" aria-hidden="true" />
                )}
                <button
                  type="button"
                  data-nav-item
                  role="option"
                  aria-selected={false}
                  className={`dsg-nav-item dsg-nav-l${Math.min(it.level, 4)}`}
                  onClick={() => onJump(it)}
                  title={it.text}
                >
                  {it.prefix && <span className="dsg-nav-prefix">{it.prefix}</span>}
                  <span className="dsg-nav-text">{it.text || <em>Empty paragraph</em>}</span>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
